// Icon set abstraction — different themes can use different icon libraries
// This gives each theme a truly distinct visual feel

import type { ComponentType } from 'react';

// Lucide icons (default)
import {
  LayoutDashboard as LucideLayoutDashboard,
  Users as LucideUsers,
  PiggyBank as LucidePiggyBank,
  Settings as LucideSettings,
  User as LucideUser,
  LogOut as LucideLogOut,
  Menu as LucideMenu,
  X as LucideX,
  ChevronDown as LucideChevronDown,
  ChevronRight as LucideChevronRight,
  Shield as LucideShield,
  CreditCard as LucideCreditCard,
  Lock as LucideLock,
  MoreHorizontal as LucideMoreHorizontal,
} from 'lucide-react';

// Phosphor icons (Aurora theme — rounder, bolder, more playful)
import {
  SquaresFour as PhSquaresFour,
  UsersThree as PhUsersThree,
  PiggyBank as PhPiggyBank,
  GearSix as PhGearSix,
  UserCircle as PhUserCircle,
  SignOut as PhSignOut,
  List as PhList,
  X as PhX,
  CaretDown as PhCaretDown,
  CaretRight as PhCaretRight,
  ShieldCheck as PhShieldCheck,
  CreditCard as PhCreditCard,
  LockSimple as PhLockSimple,
  DotsThreeOutline as PhDotsThreeOutline,
} from '@phosphor-icons/react';

export type IconName =
  | 'dashboard'
  | 'users'
  | 'piggyBank'
  | 'settings'
  | 'user'
  | 'logOut'
  | 'menu'
  | 'close'
  | 'chevronDown'
  | 'chevronRight'
  | 'shield'
  | 'creditCard'
  | 'lock'
  | 'moreHorizontal';

export type IconSet = 'lucide' | 'phosphor';

type IconProps = { className?: string; size?: string | number };
type IconComponent = ComponentType<IconProps>;

const lucideIcons: Record<IconName, IconComponent> = {
  dashboard: LucideLayoutDashboard,
  users: LucideUsers,
  piggyBank: LucidePiggyBank,
  settings: LucideSettings,
  user: LucideUser,
  logOut: LucideLogOut,
  menu: LucideMenu,
  close: LucideX,
  chevronDown: LucideChevronDown,
  chevronRight: LucideChevronRight,
  shield: LucideShield,
  creditCard: LucideCreditCard,
  lock: LucideLock,
  moreHorizontal: LucideMoreHorizontal,
};

const phosphorIcons: Record<IconName, IconComponent> = {
  dashboard: PhSquaresFour,
  users: PhUsersThree,
  piggyBank: PhPiggyBank,
  settings: PhGearSix,
  user: PhUserCircle,
  logOut: PhSignOut,
  menu: PhList,
  close: PhX,
  chevronDown: PhCaretDown,
  chevronRight: PhCaretRight,
  shield: PhShieldCheck,
  creditCard: PhCreditCard,
  lock: PhLockSimple,
  moreHorizontal: PhDotsThreeOutline,
};

const iconSets: Record<IconSet, Record<IconName, IconComponent>> = {
  lucide: lucideIcons,
  phosphor: phosphorIcons,
};

export function getIcon(set: IconSet, name: IconName): IconComponent {
  return iconSets[set]?.[name] ?? lucideIcons[name];
}

// Map from Lucide component reference to IconName (for navigation array compatibility)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lucideComponentToName = new Map<any, IconName>([
  [LucideLayoutDashboard, 'dashboard'],
  [LucideUsers, 'users'],
  [LucidePiggyBank, 'piggyBank'],
  [LucideSettings, 'settings'],
  [LucideUser, 'user'],
  [LucideCreditCard, 'creditCard'],
  [LucideShield, 'shield'],
]);

export function resolveNavIcon(
  set: IconSet,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  originalIcon: any
): IconComponent {
  const name = lucideComponentToName.get(originalIcon);
  if (!name) return originalIcon as IconComponent;
  return getIcon(set, name);
}
